/*!
 * vue-dermis v0.10.22 
 * (c) 2023 Alex Tkachenko
 * Released under the MIT License.
 */
import { withDefaultProps } from '@aspectus/vue-utils';
import { Tag } from '@aspectus/vue-tag';
import { createBlockGenerator } from '@aspectus/bem';
import { generator } from '@aspectus/vue-bem-styled-tag';
import { mapSame } from '@aspectus/vue-bem-styling';

function _typeof(obj) {
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var b = createBlockGenerator({
  n: 'ds-'
});
var t = createBlockGenerator({
  n: 't-'
});
var u = createBlockGenerator({
  n: 'u-'
});
var ud = createBlockGenerator({
  n: 'u-',
  v: '-'
});

/**
 * Private `concat` function to merge two array-like objects.
 *
 * @private
 * @param {Array|Arguments} [set1=[]] An array-like object.
 * @param {Array|Arguments} [set2=[]] An array-like object.
 * @return {Array} A new, merged array.
 * @example
 *
 *      _concat([4, 5, 6], [1, 2, 3]); //=> [4, 5, 6, 1, 2, 3]
 */
function _concat(set1, set2) {
  set1 = set1 || [];
  set2 = set2 || [];
  var idx;
  var len1 = set1.length;
  var len2 = set2.length;
  var result = [];
  idx = 0;

  while (idx < len1) {
    result[result.length] = set1[idx];
    idx += 1;
  }

  idx = 0;

  while (idx < len2) {
    result[result.length] = set2[idx];
    idx += 1;
  }

  return result;
}

var _concat_1 = _concat;

function _arity(n, fn) {
  /* eslint-disable no-unused-vars */
  switch (n) {
    case 0:
      return function () {
        return fn.apply(this, arguments);
      };

    case 1:
      return function (a0) {
        return fn.apply(this, arguments);
      };

    case 2:
      return function (a0, a1) {
        return fn.apply(this, arguments);
      };

    case 3:
      return function (a0, a1, a2) {
        return fn.apply(this, arguments);
      };

    case 4:
      return function (a0, a1, a2, a3) {
        return fn.apply(this, arguments);
      };

    case 5:
      return function (a0, a1, a2, a3, a4) {
        return fn.apply(this, arguments);
      };

    case 6:
      return function (a0, a1, a2, a3, a4, a5) {
        return fn.apply(this, arguments);
      };

    case 7:
      return function (a0, a1, a2, a3, a4, a5, a6) {
        return fn.apply(this, arguments);
      };

    case 8:
      return function (a0, a1, a2, a3, a4, a5, a6, a7) {
        return fn.apply(this, arguments);
      };

    case 9:
      return function (a0, a1, a2, a3, a4, a5, a6, a7, a8) {
        return fn.apply(this, arguments);
      };

    case 10:
      return function (a0, a1, a2, a3, a4, a5, a6, a7, a8, a9) {
        return fn.apply(this, arguments);
      };

    default:
      throw new Error('First argument to _arity must be a non-negative integer no greater than ten');
  }
}

var _arity_1 = _arity;

function _isPlaceholder(a) {
  return a != null && _typeof(a) === 'object' && a['@@functional/placeholder'] === true;
}

var _isPlaceholder_1 = _isPlaceholder;

/**
 * Optimized internal one-arity curry function.
 *
 * @private
 * @category Function
 * @param {Function} fn The function to curry.
 * @return {Function} The curried function.
 */

function _curry1(fn) {
  return function f1(a) {
    if (arguments.length === 0 || _isPlaceholder_1(a)) {
      return f1;
    } else {
      return fn.apply(this, arguments);
    }
  };
}

var _curry1_1 = _curry1;

/**
 * Optimized internal two-arity curry function.
 *
 * @private
 * @category Function
 * @param {Function} fn The function to curry.
 * @return {Function} The curried function.
 */

function _curry2(fn) {
  return function f2(a, b) {
    switch (arguments.length) {
      case 0:
        return f2;

      case 1:
        return _isPlaceholder_1(a) ? f2 : _curry1_1(function (_b) {
          return fn(a, _b);
        });

      default:
        return _isPlaceholder_1(a) && _isPlaceholder_1(b) ? f2 : _isPlaceholder_1(a) ? _curry1_1(function (_a) {
          return fn(_a, b);
        }) : _isPlaceholder_1(b) ? _curry1_1(function (_b) {
          return fn(a, _b);
        }) : fn(a, b);
    }
  };
}

var _curry2_1 = _curry2;

function _createPartialApplicator(concat) {
  return _curry2_1(function (fn, args) {
    return _arity_1(Math.max(0, fn.length - args.length), function () {
      return fn.apply(this, concat(args, arguments));
    });
  });
}

var _createPartialApplicator_1 = _createPartialApplicator;

/**
 * Takes a function `f` and a list of arguments, and returns a function `g`.
 * When applied, `g` returns the result of applying `f` to the arguments
 * provided initially followed by the arguments provided to `g`.
 *
 * @func
 * @memberOf R
 * @since v0.10.0
 * @category Function
 * @sig ((a, b, c, ..., n) -> x) -> [a, b, c, ...] -> ((d, e, f, ..., n) -> x)
 * @param {Function} f
 * @param {Array} args
 * @return {Function}
 * @see R.partialRight, R.curry
 * @example
 *
 *      const multiply2 = (a, b) => a * b;
 *      const double = R.partial(multiply2, [2]);
 *      double(2); //=> 4
 *
 *      const greet = (salutation, title, firstName, lastName) =>
 *        salutation + ', ' + title + ' ' + firstName + ' ' + lastName + '!';
 *
 *      const sayHello = R.partial(greet, ['Hello']);
 *      const sayHelloToMs = R.partial(sayHello, ['Ms.']);
 *      sayHelloToMs('Jane', 'Jones'); //=> 'Hello, Ms. Jane Jones!'
 * @symb R.partial(f, [a, b])(c, d) = f(a, b, c, d)
 */

var partial =
/*#__PURE__*/
_createPartialApplicator_1(_concat_1);

var partial_1 = partial;

var tag = function tag(name) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var bg = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : b;
  var block = bg(name);
  return [Object.assign(generator(_objectSpread2({
    tag: Tag,
    block: block
  }, options)), {
    name: name
  }), block];
};
var el = function el(name, bl) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var blockName = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : name;
  return Object.assign(generator(_objectSpread2({
    tag: Tag,
    block: partial_1(bl, [blockName])
  }, options)), {
    name: name
  });
};
var inner = function inner(name) {
  return function (h, context, _ref) {
    var block = _ref.block;
    return [h('div', {
      class: block(name)
    }, context.children)];
  };
};
var body = inner('body');

var SIZE = mapSame('size');
var SPACE = mapSame('space');
var IN_OF = mapSame('indent', 'offset');

var dtag = function dtag(tg) {
  return withDefaultProps({
    tag: tg
  }, Tag);
};

var _tag = tag('caption', {
  modifiers: SIZE
}),
    _tag2 = _slicedToArray(_tag, 1),
    Caption = _tag2[0];

var _tag3 = tag('inliner', {
  modifiers: SIZE,
  children: body
}),
    _tag4 = _slicedToArray(_tag3, 1),
    Inliner = _tag4[0];

var _tag5 = tag('link'),
    _tag6 = _slicedToArray(_tag5, 1),
    Link = _tag6[0];

var _tag7 = tag('aspect-ratio', {
  modifiers: mapSame('ratio')
}),
    _tag8 = _slicedToArray(_tag7, 2),
    AspectRatio = _tag8[0],
    aspectRatioBlock = _tag8[1];
var AspectRatioBody = el('aspect-ratio-body', aspectRatioBlock, 'body');

var _tag9 = tag('panel', {
  modifiers: SPACE
}),
    _tag10 = _slicedToArray(_tag9, 2),
    Panel = _tag10[0],
    panelBlock = _tag10[1];
var PanelElement = el('panel-element', panelBlock, {
  modifiers: IN_OF
}, 'element');

var _tag11 = tag('section', {
  modifiers: SPACE
}),
    _tag12 = _slicedToArray(_tag11, 2),
    Section = _tag12[0],
    sectionBlock = _tag12[1];
var SectionElement = el('section-element', sectionBlock, {
  modifiers: IN_OF
}, 'element');

var _tag13 = tag('table'),
    _tag14 = _slicedToArray(_tag13, 2),
    Table = _tag14[0],
    tableBlock = _tag14[1];
var TableCaption = el('table-caption', tableBlock, {
  tag: dtag('caption')
}, 'caption');
var TableRow = el('table-row', tableBlock, {
  tag: dtag('tr'),
  modifiers: mapSame('relief')
}, 'row');
var TableCell = el('table-cell', tableBlock, {
  tag: dtag('td'),
  modifiers: mapSame('head')
}, 'cell'); // Typography

var _tag15 = tag('content', {}, t),
    _tag16 = _slicedToArray(_tag15, 1),
    Content = _tag16[0];

var _tag17 = tag('words', {
  modifiers: mapSame('fill')
}, t),
    _tag18 = _slicedToArray(_tag17, 1),
    Words = _tag18[0]; // Utils

var _tag19 = tag('display', {
  modifiers: mapSame('hidden', 'block')
}, ud),
    _tag20 = _slicedToArray(_tag19, 2),
    Display = _tag20[0],
    displayBlock = _tag20[1];

var _tag21 = tag('spreaded-interactor', {}, u),
    _tag22 = _slicedToArray(_tag21, 2),
    SpreadedInteractor = _tag22[0],
    spreadedInteractorBlock = _tag22[1];

var children = function children(h, context, _ref) {
  var block = _ref.block;

  if (context.children) {
    return context.scopedSlots.default(_objectSpread2({
      block: block
    }, context.props));
  }

  var result = [];
  var _context$props = context.props,
      src = _context$props.src,
      label = _context$props.label;

  if (src) {
    result.push(h('img', {
      class: block('image'),
      attrs: {
        src: src,
        alt: label
      }
    }));
  } else if (label && label.charAt) {
    result.push(h('div', {
      class: block('label')
    }, [label.charAt(0)]));
  }

  return result;
};

var _tag$1 = tag('avatar', {
  modifiers: SIZE,
  props: {
    src: String,
    label: String
  },
  children: children
}),
    _tag2$1 = _slicedToArray(_tag$1, 1),
    Avatar = _tag2$1[0];

function install(Vue) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$prefix = _ref.prefix,
      prefix = _ref$prefix === void 0 ? 'ds-' : _ref$prefix,
      _ref$prefixType = _ref.prefixType,
      prefixType = _ref$prefixType === void 0 ? 't-' : _ref$prefixType,
      _ref$prefixUtils = _ref.prefixUtils,
      prefixUtils = _ref$prefixUtils === void 0 ? 'u-' : _ref$prefixUtils;

  Vue.component(prefix + Caption.name, Caption);
  Vue.component(prefix + Inliner.name, Inliner);
  Vue.component(prefix + Link.name, Link);
  Vue.component(prefix + Avatar.name, Avatar);
  Vue.component(prefix + AspectRatio.name, AspectRatio);
  Vue.component(prefix + AspectRatioBody.name, AspectRatioBody);
  Vue.component(prefix + Panel.name, Panel);
  Vue.component(prefix + PanelElement.name, PanelElement);
  Vue.component(prefix + Section.name, Section);
  Vue.component(prefix + SectionElement.name, SectionElement);
  Vue.component(prefix + Table.name, Table);
  Vue.component(prefix + TableCaption.name, TableCaption);
  Vue.component(prefix + TableRow.name, TableRow);
  Vue.component(prefix + TableCell.name, TableCell);
  Vue.component(prefixType + Content.name, Content);
  Vue.component(prefixType + Words.name, Words);
  Vue.component(prefixUtils + Display.name, Display);
  Vue.component(prefixUtils + SpreadedInteractor.name, SpreadedInteractor);
}
var main = {
  install: install
};

export default main;
export { AspectRatio, AspectRatioBody, Avatar, Caption, Content, Display, Inliner, Link, Panel, PanelElement, Section, SectionElement, SpreadedInteractor, Table, TableCaption, TableCell, TableRow, Words, aspectRatioBlock, displayBlock, el, inner, install, panelBlock, sectionBlock, spreadedInteractorBlock, tableBlock, tag };
